import React, { useState, useRef, useEffect } from 'react';
import { Code, Eye, X, Save, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

const EDITOR_MODES = {
  PREVIEW: 'preview',
  CODE: 'code'
};

const formatHtml = (html: string) => {
  // Basic HTML formatting to ensure proper paragraph spacing
  return html
    .replace(/<\/p><p>/g, '</p>\n<p>') // Add newline between paragraphs
    .replace(/<\/h\d><h\d>/g, '</h$1>\n<h$2>') // Add newline between headers
    .replace(/<\/ul><ul>/g, '</ul>\n<ul>') // Add newline between lists
    .replace(/<\/li><li>/g, '</li>\n<li>'); // Add newline between list items
};

const EditPanel = ({ 
  value, 
  onSave, 
  onCancel 
}: { 
  value: string;
  onSave: (newValue: string) => Promise<void>;
  onCancel: () => void;
}) => {
  const { t } = useTranslation();
  const [editValue, setEditValue] = useState(formatHtml(value));
  const [isSaving, setIsSaving] = useState(false);
  const [mode, setMode] = useState(EDITOR_MODES.PREVIEW);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (previewRef.current) {
      previewRef.current.innerHTML = editValue;
    }
  }, [mode]);

  const handlePreviewChange = () => {
    if (previewRef.current) {
      const newContent = previewRef.current.innerHTML;
      setEditValue(formatHtml(newContent));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-4 mx-4 flex flex-col h-[80vh]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-lg">{t("editPanel.edit_content")}</h3>
          <button 
            onClick={onCancel}
            className="p-1.5 hover:bg-gray-100 rounded-full"
            aria-label={t("editPanel.cancel")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mode Switcher */}
        <div className="flex space-x-2 mb-3">
          <button
            onClick={() => setMode(EDITOR_MODES.PREVIEW)}
            className={cn(
              "px-3 py-1.5 rounded-lg flex items-center space-x-2 text-sm",
              mode === EDITOR_MODES.PREVIEW ? "bg-blue-50 text-blue-700" : "hover:bg-gray-100"
            )}
          >
            <Eye className="w-4 h-4" />
            <span>{t("editPanel.shopper_view")}</span>
          </button>
          <button
            onClick={() => setMode(EDITOR_MODES.CODE)}
            className={cn(
              "px-3 py-1.5 rounded-lg flex items-center space-x-2 text-sm",
              mode === EDITOR_MODES.CODE ? "bg-blue-50 text-blue-700" : "hover:bg-gray-100"
            )}
          >
            <Code className="w-4 h-4" />
            <span>{t("editPanel.code_view")}</span>
          </button>
        </div>

        {/* Editor Content */}
        <div className="flex-grow overflow-hidden relative">
          {mode === EDITOR_MODES.PREVIEW ? (
            <div 
              ref={previewRef}
              contentEditable
              onInput={handlePreviewChange}
              className="h-full p-3 border rounded-lg overflow-y-auto bg-white prose prose-sm max-w-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ minHeight: '200px' }}
            />
          ) : (
            <textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-full h-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm resize-none"
              spellCheck={false}
              style={{ minHeight: '200px' }}
            />
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-2 mt-3">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            {t("editPanel.cancel")}
          </button>
          <button
            disabled={isSaving}
            onClick={async () => {
              setIsSaving(true);
              await onSave(editValue);
              setIsSaving(false);
            }}
            className="flex items-center px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
          >
            {isSaving && <RefreshCw className="w-4 h-4 animate-spin mx-2" />}
            <Save className="w-4 h-4 mr-2" />
            {t("editPanel.save_changes")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPanel;